import React, { useMemo, memo } from "react";
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    createColumnHelper,
} from "@tanstack/react-table";

const EMPTY = [];

const calcVariance = (current, base) => {

    if (
        current === null ||
        current === undefined ||
        base === null ||
        base === undefined ||
        base === 0 ||
        base === "-"
    ) return "-";

    const num =
        ((current - base) / base) * 100;

    if (!Number.isFinite(num))
        return "-";

    return num.toFixed(2) + "%";
};

const VarCell = memo((info) => {

    const val = info.getValue();

    if (!val || val === "-")
        return "-";

    const num = parseFloat(val);

    if (!Number.isFinite(num))
        return val;

    return (

        <span
            className={
                num >= 0
                    ? "text-green-600 font-semibold"
                    : "text-red-500 font-semibold"
            }
        >
            {val}
        </span>

    );

});

const columnHelper = createColumnHelper();

const columns = [

    columnHelper.accessor("name", {
        header: "Area",
        cell: (info) => (
            <span className="font-semibold text-left block">
                {info.getValue()}
            </span>
        )
    }),

    columnHelper.group({

        id: "retailGroup",

        header: "Retail",

        columns: [

            columnHelper.accessor("opStock", { header: "Op. Stock" }),

            columnHelper.accessor("retTgt", { header: "Month Tgt" }),

            columnHelper.accessor("retMtdTgt", { header: "Ret MTD Tgt" }),

            columnHelper.group({

                header: "Actuals",

                columns: [

                    columnHelper.accessor("retMtd", { header: "Retail MTD" }),

                    columnHelper.accessor("retLmtd", { header: "Retail LMTD" }),

                    columnHelper.accessor("retLysm", { header: "Retail LYSM MTD" }),

                    columnHelper.accessor("retTotLm", { header: "Total LM" }),

                    columnHelper.accessor("retTotLysm", { header: "Total LYSM" }),

                ]

            }),

            columnHelper.accessor("retMtdAch", { header: "MTD Ach%" }),

            columnHelper.group({

                header: "Variance %",

                columns: [

                    columnHelper.accessor(
                        "retVarLmtd",
                        {
                            header: "Ret LMTD",
                            cell: VarCell
                        }
                    ),

                    columnHelper.accessor(
                        "retVarOct",
                        {
                            header: "Retail LYSM MTD",
                            cell: VarCell
                        }
                    ),

                ]

            }),

        ]

    }),

    columnHelper.group({

        id: "offtakeGroup",

        header: "Offtake",

        columns: [

            columnHelper.group({

                header: "Actuals",

                columns: [

                    columnHelper.accessor("offMtd", { header: "MTD" }),

                    columnHelper.accessor("offLmtd", { header: "LMTD" }),

                    columnHelper.accessor("offTotLm", { header: "Total LM" }),

                ]

            }),

            columnHelper.group({

                header: "Var%",

                columns: [

                    columnHelper.accessor(
                        "offVarLmtd",
                        {
                            header: "vs LMTD",
                            cell: VarCell
                        }
                    ),

                ]

            }),

        ]

    }),

    columnHelper.accessor(
        "mtdStock",
        { header: "MTD Stock" }
    ),

];

const Zoneperformbystate = ({ retailstate, offtakestate }) => {

    const retailData =
        useMemo(
            () => retailstate?.data || EMPTY,
            [retailstate]
        );

    const offtakeData =
        useMemo(
            () => offtakestate?.data || EMPTY,
            [offtakestate]
        );

    const offtakeMap =
        useMemo(() => {

            const map =
                Object.create(null);

            for (const o of offtakeData) {

                if (o?.key) {
                    map[o.key] = o;
                }

            }

            return map;

        }, [offtakeData]);

    const rows = useMemo(() => {

        if (!retailData.length)
            return EMPTY;

        const baseRows = retailData.map((retail) => {

            const off = offtakeMap[retail.key];

            const offMtdVal =
                off?.OFFTAKE_MTD?.value ?? 0;

            const offLmtdVal =
                off?.OFFTAKE_LMTD?.value ?? 0;

            return {

                name: retail.key ?? "-",

                opStock: retail.opening_stock ?? 0,
                retTgt: retail.month_target ?? 0,
                retMtdTgt: retail.retail_mtd_target ?? 0,

                retMtd: retail.Retail_MTD ?? 0,
                retLmtd: retail.Retail_LMTD ?? 0,
                retLysm: retail.Retail_LYSM_MTD ?? 0,

                retTotLm: retail.total_retail_last_month ?? 0,
                retTotLysm: retail.Retail_LYSM_total ?? 0,

                retMtdAch: retail.mtd_achieve ?? "-",

                retVarLmtd:
                    calcVariance(
                        retail.Retail_MTD,
                        retail.Retail_LMTD
                    ),

                retVarOct:
                    calcVariance(
                        retail.Retail_MTD,
                        retail.Retail_LYSM_MTD
                    ),

                offMtd: offMtdVal,
                offLmtd: offLmtdVal,

                offTotLm:
                    off?.OFFTAKE_MTD_LM?.value ?? 0,

                offVarLmtd:
                    calcVariance(
                        offMtdVal,
                        offLmtdVal
                    ),

                mtdStock:
                    off?.MTD_STOCK?.value ?? 0,

            };

        });

        // ✅ REMOVE rows where all values are 0
        const filteredRows = baseRows.filter(row => {

            const numericValues = [

                row.opStock,
                row.retTgt,
                row.retMtdTgt,
                row.retMtd,
                row.retLmtd,
                row.retLysm,
                row.retTotLm,
                row.retTotLysm,
                row.offMtd,
                row.offLmtd,
                row.offTotLm,
                row.mtdStock

            ];

            return numericValues.some(val => Number(val) !== 0);

        });

        const sum = (key) =>
            filteredRows.reduce(
                (acc, row) =>
                    acc + (Number(row[key]) || 0),
                0
            );

        const totalRow = {

            name: "Grand Total",

            opStock: sum("opStock"),
            retTgt: sum("retTgt"),
            retMtdTgt: sum("retMtdTgt"),

            retMtd: sum("retMtd"),
            retLmtd: sum("retLmtd"),
            retLysm: sum("retLysm"),

            retTotLm: sum("retTotLm"),
            retTotLysm: sum("retTotLysm"),

            retMtdAch:
                sum("retMtdTgt") > 0
                    ? ((sum("retMtd") / sum("retMtdTgt")) * 100).toFixed(2) + "%"
                    : "-",

            retVarLmtd:
                calcVariance(
                    sum("retMtd"),
                    sum("retLmtd")
                ),

            retVarOct:
                calcVariance(
                    sum("retMtd"),
                    sum("retLysm")
                ),

            offMtd: sum("offMtd"),
            offLmtd: sum("offLmtd"),
            offTotLm: sum("offTotLm"),

            offVarLmtd:
                calcVariance(
                    sum("offMtd"),
                    sum("offLmtd")
                ),

            mtdStock: sum("mtdStock")

        };

        return [
            ...filteredRows,
            totalRow
        ];

    }, [retailData, offtakeMap]);
    const table = useReactTable({

        data: rows,

        columns,

        getCoreRowModel: getCoreRowModel()

    });

    return (

        <div className="bg-white rounded-xl w-[400px] md:w-[1200px] mt-1 shadow-sm border border-slate-200 overflow-hidden ml-2 p-2">

            <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-3 pl-1">
                By State / Area
            </h3>

            <div className="overflow-x-auto overflow-y-auto max-h-[350px] border border-slate-200 rounded-xl shadow-sm bg-white">

                <table
                    id="zoneStatePerformanceTable"
                    className="w-full min-w-max border-collapse"
                >

                    <thead>

                        {table.getHeaderGroups().map((headerGroup) => (

                            <tr key={headerGroup.id}>

                                {headerGroup.headers.map((header) => (

                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        className="border border-slate-300 px-1 py-2 text-center text-[11px] bg-[#234b63] text-white sticky top-0 z-10"
                                    >

                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}

                                    </th>

                                ))}

                            </tr>

                        ))}

                    </thead>

                    <tbody>

                        {table.getRowModel().rows.map((row) => {

                            const isTotal =
                                row.original.name
                                    ?.toLowerCase()
                                    .includes("total");

                            return (

                                <tr
                                    key={row.id}
                                    className={
                                        isTotal
                                            ? "bg-blue-100 font-bold"
                                            : "hover:bg-slate-50"
                                    }
                                >

                                    {row.getVisibleCells().map((cell) => (

                                        <td
                                            key={cell.id}
                                            className="px-2 py-2 border border-slate-200 text-[11px] text-center"
                                        >

                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}

                                        </td>

                                    ))}

                                </tr>

                            );

                        })}

                    </tbody>

                </table>

            </div>

        </div>

    );

};

export default Zoneperformbystate;